import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  LoadingOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import dayjs from 'dayjs';
import { NotificationContext } from 'context/NotificationContext';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';

import TooltipPopover from 'components/TooltipPopover/TooltipPopover';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import StyledCheckbox from 'components/StyledCheckbox/StyledCheckbox';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import {
  DENTAL_PLAN_CHANNEL,
  MEDICAL_PLAN_CHANNEL,
  OPEN_ADD_NEW_PLAN_MODAL,
  VISION_PLAN_CHANNEL,
} from 'modules/clients/UPClient/UPClientConst';
import {
  addPlansDatedTooltipContent,
  addPlansTooltipContent,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import FixedAlertMessage from 'components/FixedAlert/FixedAlertMessage';
import { coversDate } from 'modules/effectiveDate/utils/guideScope';
import { getPlanYears } from 'modules/home/slices/basicInfoSlice';
import {
  getFormattedInputDate,
  LONG_MONTH_DATE_FORMAT,
  MMDDYYYY_SLASHES_FORMAT,
} from 'utils/dateUtil';

import MDVPlan from 'model/MDVPlan';
import Plan from 'model/Plan';
import {
  ERROR_MESSAGE_PLAN_EDITING,
  SPANISH_MODE_PLAN_MANAGEMENT_TOOLTIP,
} from 'constants/commonConstants';
import { usePrevious } from 'hooks/usePrevious';

import styles from './AddOrRemovePlans.module.less';

const NO_PLAN_YEAR_COVERS_DATE = 'No plan year covers this date';

const MDVChannels = [
  MEDICAL_PLAN_CHANNEL,
  DENTAL_PLAN_CHANNEL,
  VISION_PLAN_CHANNEL,
];

type AddOrRemovePlansProps = {
  onClose?: Function;
  visible: boolean;
  title: string;
  withoutModal?: boolean;
  onSave: Function;
  addedPlanList: (MDVPlan | Plan)[] | [];
  allPlans: (MDVPlan | Plan)[];
  reloadPlans: Function;
  channel?: string;
  actionContent?: ReactNode;
  isLoading?: boolean;
  showAlert: Function;
};

type PlanItem = {
  plan: MDVPlan | Plan;
  status: 'ADDED' | 'REMOVED';
  isInInitialList: boolean;
};

const AddOrRemovePlans: FC<AddOrRemovePlansProps> = (
  props: AddOrRemovePlansProps
) => {
  const {
    onClose,
    visible,
    title,
    withoutModal,
    onSave,
    addedPlanList,
    allPlans,
    reloadPlans,
    channel = MEDICAL_PLAN_CHANNEL,
    actionContent,
    isLoading,
    showAlert,
  } = props;

  const benguide = useBenguide();
  const { benefitClasses, planYearId, employerId, storedEffectiveDate } =
    benguide;
  const language = useAppSelector((state) => state.benguide.data?.language);
  const dispatch = useAppDispatch();

  // Persisted data decides whether this is a dated guide. Older guides that
  // were legitimately saved without a date keep their scalar-year behavior.
  const datedMode = Boolean(storedEffectiveDate);
  const planYears = useAppSelector((state) => state.basicInfo.planYears);
  // A boolean, not the array: the read's first action assigns a fresh empty
  // array, and a failed or empty answer leaves the list empty, so depending on
  // the array would ask again forever.
  const planYearsEmpty = isEmpty(planYears);
  useEffect(() => {
    if (datedMode && employerId && planYearsEmpty) {
      dispatch(getPlanYears(employerId));
    }
  }, [datedMode, employerId, planYearsEmpty, dispatch]);
  // A line can only get a new plan from a plan year that covers the date, and
  // the portal cannot tell the editor not to offer the button.
  const noPlanYearCoversDate =
    datedMode &&
    !isEmpty(planYears) &&
    !planYears.some((planYear) =>
      coversDate(planYear, storedEffectiveDate as string)
    );

  // A ticked plan the date's list no longer offers (its plan year stopped
  // covering the date): shown marked, and left out when the line is saved.
  // The list never offers a hidden plan, so a held plan whose own plan year
  // still covers the date is kept, as the server keeps it. With no plan years
  // loaded yet the plan cannot be judged, so nothing is marked.
  // A plan missing only because the guide's classes no longer match is not
  // stale; it stays as it is today. An empty list may be a failed read (the
  // plain thunks leave `[]` behind), so it can never mark anything: Save would
  // otherwise drop every attached plan.
  const staleIds = useMemo(() => {
    if (!datedMode || isEmpty(allPlans) || planYearsEmpty) {
      return [];
    }
    const offered = new Set(allPlans.map((plan) => plan.id));
    return addedPlanList
      .filter(
        (plan) =>
          !offered.has(plan.id) &&
          !planYears.some(
            (year) =>
              year.id === plan.planYearId &&
              coversDate(year, storedEffectiveDate as string)
          ) &&
          (!benefitClasses ||
            (plan.groups ?? []).some((group) => benefitClasses.includes(group)))
      )
      .map((plan) => plan.id);
  }, [
    datedMode,
    allPlans,
    addedPlanList,
    benefitClasses,
    planYears,
    planYearsEmpty,
    storedEffectiveDate,
  ]);

  const [selectedPlans, setSelectedPlans] =
    useState<(MDVPlan | Plan)[]>(addedPlanList);
  const [selectedPlanItems, setSelectedPlanItems] = useState<PlanItem[]>([]);
  const [isNotificationReceived, setNotificationReceived] =
    useState<boolean>(false);
  const [isPlanCreatingMode, setPlanCreatingMode] = useState<boolean>(false);
  const [isPlanAdded, setPlanAdded] = useState<boolean>(false);
  const notificationFlag = useContext(NotificationContext);

  const prevAllPlans = usePrevious(allPlans);

  const setNotificationReceiveFlag = useCallback(() => {
    if (notificationFlag) {
      setNotificationReceived(true);
    }
  }, [notificationFlag]);

  useEffect(() => {
    setSelectedPlans(addedPlanList);
    const data = [...addedPlanList].map((plan: MDVPlan | Plan) => {
      return {
        plan: plan,
        status: 'ADDED',
        isInInitialList: true,
      } as PlanItem;
    });
    setSelectedPlanItems(data);
    setNotificationReceiveFlag();
  }, [visible, addedPlanList, setNotificationReceiveFlag]);

  useEffect(() => {
    if (visible) {
      reloadPlans();
    }
  }, [reloadPlans, visible]);

  const addPlan = useCallback(
    (plan: MDVPlan | Plan) => {
      const updated = [...selectedPlanItems].map((planItem: PlanItem) => {
        if (
          planItem.plan.id === plan.id &&
          planItem.isInInitialList &&
          planItem.status === 'REMOVED'
        ) {
          return {
            plan: plan,
            status: 'ADDED',
            isInInitialList: true,
          } as PlanItem;
        } else if (
          planItem.plan.id === plan.id &&
          !planItem.isInInitialList &&
          planItem.status === 'REMOVED'
        ) {
          return {
            plan: plan,
            status: 'ADDED',
            isInInitialList: false,
          } as PlanItem;
        } else {
          return planItem;
        }
      });
      const filteredByPlanId = updated.filter((t) => t.plan.id === plan.id);
      if (filteredByPlanId.length === 0) {
        updated.push({
          plan: plan,
          status: 'ADDED',
          isInInitialList: false,
        } as PlanItem);
      }
      setSelectedPlanItems(updated);
      const updatedPlans = [plan, ...selectedPlans];
      setSelectedPlans(updatedPlans);
    },
    [selectedPlanItems, selectedPlans]
  );

  const removePlan = (planId: string) => {
    const updated = [...selectedPlanItems].map((planItem: PlanItem) => {
      if (planItem.plan.id === planId) {
        return {
          plan: planItem.plan,
          status: 'REMOVED',
          isInInitialList: planItem.isInInitialList,
        } as PlanItem;
      } else {
        return planItem;
      }
    });
    setSelectedPlanItems(updated);
    const updatedPlans = selectedPlans.filter((plan) => plan.id !== planId);
    setSelectedPlans(updatedPlans);
  };

  const saveSelectedPlans = useCallback(() => {
    let count = 1;
    const sortedNewList = selectedPlanItems
      .filter((item) => item.status === 'ADDED' && !item.isInInitialList)
      .sort((a, b) => a.plan.name.localeCompare(b.plan.name))
      .map((item) => item.plan);
    const existingList = selectedPlanItems
      .filter(
        (item) =>
          item.status === 'ADDED' &&
          item.isInInitialList &&
          !staleIds.includes(item.plan.id)
      )
      .map((item) => item.plan);
    const completePlans = [...sortedNewList, ...existingList].map(
      (plan: MDVPlan | Plan) => {
        // The attach body takes the plan object as it is, so the dated
        // caption data must not travel with it.
        return { ...omit(plan, 'planForDate'), orderNo: count++ } as Plan;
      }
    );
    const updatedplans = existingList?.length !== selectedPlanItems?.length;
    onSave(completePlans, false, updatedplans);
    onClose && onClose();
    setPlanAdded(false);
  }, [onClose, onSave, selectedPlanItems, staleIds]);

  useEffect(() => {
    if (isPlanCreatingMode && allPlans.length > prevAllPlans.length) {
      const newPlan = allPlans.find(
        (plan) =>
          !prevAllPlans.map((prePlan: MDVPlan) => prePlan.id).includes(plan.id)
      );
      if (newPlan) {
        addPlan(newPlan);
        setPlanAdded(true);
      }
    }
  }, [allPlans, prevAllPlans, isPlanCreatingMode, addPlan]);

  useEffect(() => {
    if (isPlanCreatingMode && isPlanAdded) {
      showAlert();
      saveSelectedPlans();
      setPlanCreatingMode(false);
    }
  }, [isPlanCreatingMode, isPlanAdded, saveSelectedPlans, showAlert]);

  const isPlanAlreadyAdded = (planId: string) => {
    return (
      selectedPlans.find((plan: MDVPlan | Plan) => plan.id === planId) !==
      undefined
    );
  };

  const isNotificationAlertVisible = () => {
    return isNotificationReceived && !isEmpty(addedPlanList);
  };

  let dbgPlans: (MDVPlan | Plan)[] = [];
  dbgPlans = dbgPlans.concat(allPlans);
  addedPlanList.forEach((plan) => {
    if (plan.planYearId !== planYearId) {
      dbgPlans.push(plan);
    } else if (
      benefitClasses &&
      !plan.groups.some((value) => benefitClasses.includes(value))
    ) {
      dbgPlans.push(plan);
    }
  });

  const hasSelectedPlans = selectedPlanItems.some(
    (item) => item.status === 'ADDED'
  );

  // uniqBy keeps the first copy, the one from allPlans, which carries planForDate.
  const allPlansSorted = uniqBy([...dbgPlans, ...addedPlanList], 'id').sort(
    (a, b) => a?.name?.localeCompare(b?.name)
  );

  const hasPlan = allPlansSorted && allPlansSorted.length !== 0;

  // The plans' own date, not the guide's: a plan can have taken effect before
  // the guide date. The plans in force are the ones with the latest date on or
  // before the guide date; later or undated plans do not count. ISO dates, so
  // string order is date order.
  const plansTookEffect = allPlans
    .map((plan) => plan.planForDate?.effectiveDate)
    .filter(
      (date): date is string =>
        !!date && date <= (storedEffectiveDate as string)
    )
    .sort()
    .pop();

  const upClient = useUPClient();

  const modalBody = (
    <div
      className={`${styles.addOrRemovePlansWrapper} ${
        !withoutModal && styles.planModalWrapper
      }`}
    >
      <div className={styles.header}>
        <div>
          <PlusCircleOutlined className={styles.addIcon} />
        </div>
        <div>
          <div className={styles.title}>{`${title} Plans`}</div>
          <div className={styles.noPlansHeader}>
            {hasPlan || isLoading ? (
              datedMode ? (
                <>
                  {`Here are the plans in force on ${getFormattedInputDate(
                    storedEffectiveDate as string,
                    LONG_MONTH_DATE_FORMAT
                  )}${
                    plansTookEffect
                      ? `, effective ${getFormattedInputDate(
                          plansTookEffect,
                          LONG_MONTH_DATE_FORMAT
                        )}`
                      : ''
                  }.`}
                </>
              ) : (
                <>Here are the available plans for this microsite.</>
              )
            ) : datedMode ? (
              <>No plans for this line on this date</>
            ) : (
              <>You have no {title} plans available for this microsite.</>
            )}
            <TooltipPopover
              content={
                datedMode ? addPlansDatedTooltipContent : addPlansTooltipContent
              }
              placement="top"
              className="addPlansOverlay"
            >
              <QuestionCircleOutlined className={styles.infoIcon} />
            </TooltipPopover>
          </div>
        </div>
      </div>
      {isNotificationAlertVisible() && (
        <FixedAlertMessage
          type={'error'}
          message={ERROR_MESSAGE_PLAN_EDITING}
        />
      )}
      {isLoading ? (
        <LoadingOutlined style={{ fontSize: 24 }} spin />
      ) : (
        <>
          {hasPlan && (
            <div className={styles.selectPlanText}>
              {datedMode
                ? 'Select the plans to include in this microsite:'
                : 'Select plans below to include them in this guide:'}
            </div>
          )}
          <div
            className={
              !withoutModal && !hasPlan ? styles.planNameWrapperNoPlan : ''
            }
          >
            {hasPlan && (
              <div
                className={`${styles.planNameWrapper} ${
                  isNotificationAlertVisible()
                    ? styles.heightWithNotificationAlert
                    : ''
                }`}
              >
                <div
                  className={
                    datedMode
                      ? `${styles.planNames} ${styles.datedPlanNames}`
                      : styles.planNames
                  }
                >
                  {allPlansSorted.map((plan, index) => {
                    const { name, id } = plan;
                    return (
                      <StyledCheckbox
                        key={index}
                        value={id}
                        invalid={staleIds.includes(id)}
                        checked={isPlanAlreadyAdded(id)}
                        onChange={(event) => {
                          const { target } = event;
                          const { checked } = target;
                          if (checked) {
                            addPlan(plan);
                          } else {
                            removePlan(id);
                          }
                        }}
                      >
                        <div
                          className={`${styles.planName} prevent-trigger-hotspots`}
                          title={name}
                        >
                          {name}
                        </div>
                        {plan.planForDate && (
                          <div className={styles.planDate}>
                            {`effective ${dayjs(
                              plan.planForDate.effectiveDate
                            ).format(MMDDYYYY_SLASHES_FORMAT)} · ${
                              plan.planForDate.planYearName
                            }`}
                          </div>
                        )}
                        {staleIds.includes(id) && (
                          <div className={styles.planStale}>
                            its plan year no longer covers this date · removed
                            when you save
                          </div>
                        )}
                      </StyledCheckbox>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className={styles.buttons}>
            <div
              data-id="create-new-plan"
              className={styles.createNewPlanWrapper}
              onClick={() => {
                setPlanCreatingMode(true);
              }}
            >
              {noPlanYearCoversDate ? (
                <TooltipPopover
                  content={NO_PLAN_YEAR_COVERS_DATE}
                  placement="top"
                >
                  <Button disabled={true} className={styles.darkButtonDisabled}>
                    Create New Plan
                  </Button>
                </TooltipPopover>
              ) : actionContent && channel && !MDVChannels.includes(channel) ? (
                actionContent
              ) : language === 'SPANISH' ? (
                <TooltipPopover
                  content={SPANISH_MODE_PLAN_MANAGEMENT_TOOLTIP}
                  placement="top"
                >
                  <Button disabled={true} className={styles.darkButtonDisabled}>
                    Create New Plan
                  </Button>
                </TooltipPopover>
              ) : (
                <EditableHotspot
                  alwaysVisible={false}
                  dataTestId="plan-create-new-plan"
                  useChannel={{
                    client: upClient,
                    channel: channel,
                    event: OPEN_ADD_NEW_PLAN_MODAL,
                    data: {},
                  }}
                >
                  <Button
                    disabled={notificationFlag}
                    className={!hasPlan ? styles.darkButton : ''}
                  >
                    Create New Plan
                  </Button>
                </EditableHotspot>
              )}
            </div>
            {onClose && (
              <Button
                onClick={() => {
                  setPlanAdded(false);
                  setPlanCreatingMode(false);
                  onClose?.();
                }}
              >
                Cancel
              </Button>
            )}
            {hasPlan &&
              (language === 'SPANISH' ? (
                <TooltipPopover
                  content={SPANISH_MODE_PLAN_MANAGEMENT_TOOLTIP}
                  placement="top"
                >
                  <Button
                    className={styles.addSelectedBtnDisabled}
                    disabled={true}
                    loading={isLoading}
                  >
                    Save Plans
                  </Button>
                </TooltipPopover>
              ) : (
                <Button
                  className={styles.addSelectedBtn}
                  disabled={
                    notificationFlag ||
                    (isEmpty(addedPlanList) && !hasSelectedPlans)
                  }
                  loading={isLoading}
                  onClick={saveSelectedPlans}
                >
                  Save Plans
                </Button>
              ))}
          </div>
        </>
      )}
    </div>
  );

  if (withoutModal) {
    return <div className={styles.widthoutModal}>{modalBody}</div>;
  }

  return (
    <HalfScreenModal
      visible={visible}
      onCancel={() => onClose && onClose()}
      footer={null}
      closeIcon={false}
      className={styles.addOrRemovePlansModal}
      destroyOnClose={true}
    >
      {modalBody}
    </HalfScreenModal>
  );
};

export default AddOrRemovePlans;
